import React, { useState } from 'react';
import { Send, Package, Truck, MapPin, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { products } from '../data/mock';

const Quote = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    country: '',
    productInterest: '',
    quantity: '',
    containerType: '',
    destinationPort: '',
    additionalInfo: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSelectChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission (will be replaced with actual API call)
    setTimeout(() => {
      console.log('Quote Request Submitted:', formData);
      toast({
        title: "Quote Request Received!",
        description: "Thank you! Our team will review your enquiry and respond shortly with pricing and availability information.",
      });
      setFormData({
        companyName: '',
        contactName: '',
        email: '',
        phone: '',
        country: '',
        productInterest: '',
        quantity: '',
        containerType: '',
        destinationPort: '',
        additionalInfo: ''
      });
      setIsSubmitting(false);
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1678182451047-196f22a4143e"
            alt="Request a Quote"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#1B5E20]/80"></div>
        </div>
        
        <div className="container mx-auto px-4 z-10 relative text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">Request a Quote</h1>
          <p className="text-xl text-white/95 max-w-3xl mx-auto">
            Get a detailed quotation for your African produce requirements
          </p>
        </div>
      </section>

      {/* Quote Form Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Side Information */}
              <div className="lg:col-span-1 space-y-6">
                <Card className="border-0 shadow-lg bg-gradient-to-br from-[#1B5E20] to-[#2E7D32] text-white">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold mb-4">What's Included in Your Quote?</h3>
                    <ul className="space-y-3 text-sm text-white/90">
                      <li className="flex items-start gap-2">
                        <Package className="flex-shrink-0 mt-0.5" size={16} />
                        <span>Product specifications & pricing</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Truck className="flex-shrink-0 mt-0.5" size={16} />
                        <span>Shipping costs & timelines</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <FileText className="flex-shrink-0 mt-0.5" size={16} />
                        <span>Documentation requirements</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <MapPin className="flex-shrink-0 mt-0.5" size={16} />
                        <span>Delivery to destination port</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg bg-[#F9F9F9]">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-bold text-[#333333] mb-3">Need Help?</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Our team is ready to discuss your requirements
                    </p>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="font-semibold text-[#333333]">Email:</span>
                        <a href="mailto:admin@folalabifarms.com" className="block text-[#1B5E20] hover:underline break-all">
                          admin@folalabifarms.com
                        </a>
                      </div>
                      <div>
                        <span className="font-semibold text-[#333333]">Phone:</span>
                        <a href="tel:+447803174494" className="block text-[#1B5E20] hover:underline">
                          UK: +44 7803 174494
                        </a>
                        <a href="tel:+2349038145272" className="block text-[#1B5E20] hover:underline">
                          Nigeria: +234 903 814 5272
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg bg-[#F9F9F9]">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-bold text-[#333333] mb-3">Response Time</h3>
                    <p className="text-sm text-gray-600">
                      We review quote requests and respond with pricing and availability information as quickly as possible, typically during business hours.
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Quote Form */}
              <div className="lg:col-span-2">
                <Card className="border-0 shadow-xl">
                  <CardContent className="p-8">
                    <h2 className="text-3xl font-bold text-[#333333] mb-2">Get Your Custom Quote</h2>
                    <p className="text-gray-600 mb-8">
                      Fill out the form below with your requirements, and we'll provide a detailed quotation
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-6">
                      {/* Company & Contact Info */}
                      <div className="space-y-6">
                        <h3 className="text-xl font-semibold text-[#333333] border-b border-gray-200 pb-2">
                          Company Information
                        </h3>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <Label htmlFor="companyName" className="text-[#333333] font-semibold">Company Name *</Label>
                            <Input
                              id="companyName"
                              name="companyName"
                              type="text"
                              value={formData.companyName}
                              onChange={handleChange}
                              required
                              className="mt-2 border-gray-300 focus:border-[#1B5E20] focus:ring-[#1B5E20]"
                              placeholder="Your Company Ltd"
                            />
                          </div>

                          <div>
                            <Label htmlFor="contactName" className="text-[#333333] font-semibold">Contact Name *</Label>
                            <Input
                              id="contactName"
                              name="contactName"
                              type="text"
                              value={formData.contactName}
                              onChange={handleChange}
                              required
                              className="mt-2 border-gray-300 focus:border-[#1B5E20] focus:ring-[#1B5E20]"
                              placeholder="John Doe"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <Label htmlFor="email" className="text-[#333333] font-semibold">Email Address *</Label>
                            <Input
                              id="email"
                              name="email"
                              type="email"
                              value={formData.email}
                              onChange={handleChange}
                              required
                              className="mt-2 border-gray-300 focus:border-[#1B5E20] focus:ring-[#1B5E20]"
                              placeholder="john@company.com"
                            />
                          </div>

                          <div>
                            <Label htmlFor="phone" className="text-[#333333] font-semibold">Phone Number *</Label>
                            <Input
                              id="phone"
                              name="phone"
                              type="tel"
                              value={formData.phone}
                              onChange={handleChange}
                              required
                              className="mt-2 border-gray-300 focus:border-[#1B5E20] focus:ring-[#1B5E20]"
                              placeholder="+44 123 456 7890"
                            />
                          </div>
                        </div>

                        <div>
                          <Label htmlFor="country" className="text-[#333333] font-semibold">Country *</Label>
                          <Input
                            id="country"
                            name="country"
                            type="text"
                            value={formData.country}
                            onChange={handleChange}
                            required
                            className="mt-2 border-gray-300 focus:border-[#1B5E20] focus:ring-[#1B5E20]"
                            placeholder="United Kingdom"
                          />
                        </div>
                      </div>

                      {/* Product Requirements */}
                      <div className="space-y-6">
                        <h3 className="text-xl font-semibold text-[#333333] border-b border-gray-200 pb-2">
                          Product Requirements
                        </h3>

                        <div>
                          <Label htmlFor="productInterest" className="text-[#333333] font-semibold">Product of Interest *</Label>
                          <Select
                            value={formData.productInterest}
                            onValueChange={(value) => handleSelectChange('productInterest', value)}
                          >
                            <SelectTrigger className="mt-2 border-gray-300 focus:border-[#1B5E20] focus:ring-[#1B5E20]">
                              <SelectValue placeholder="Select a product" />
                            </SelectTrigger>
                            <SelectContent>
                              {products.map((product) => (
                                <SelectItem key={product.id} value={product.name}>
                                  {product.name}
                                </SelectItem>
                              ))}
                              <SelectItem value="multiple">Multiple Products</SelectItem>
                              <SelectItem value="other">Other (Specify in additional info)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <Label htmlFor="quantity" className="text-[#333333] font-semibold">Estimated Quantity *</Label>
                            <Input
                              id="quantity"
                              name="quantity"
                              type="text"
                              value={formData.quantity}
                              onChange={handleChange}
                              required
                              className="mt-2 border-gray-300 focus:border-[#1B5E20] focus:ring-[#1B5E20]"
                              placeholder="e.g., 1 x 40ft container"
                            />
                          </div>

                          <div>
                            <Label htmlFor="containerType" className="text-[#333333] font-semibold">Container Type *</Label>
                            <Select
                              value={formData.containerType}
                              onValueChange={(value) => handleSelectChange('containerType', value)}
                            >
                              <SelectTrigger className="mt-2 border-gray-300 focus:border-[#1B5E20] focus:ring-[#1B5E20]">
                                <SelectValue placeholder="Select container type" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="20ft-dry">20ft Dry Container</SelectItem>
                                <SelectItem value="40ft-dry">40ft Dry Container</SelectItem>
                                <SelectItem value="40ft-reefer">40ft Reefer Container</SelectItem>
                                <SelectItem value="not-sure">Not Sure</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div>
                          <Label htmlFor="destinationPort" className="text-[#333333] font-semibold">Destination Port *</Label>
                          <Input
                            id="destinationPort"
                            name="destinationPort"
                            type="text"
                            value={formData.destinationPort}
                            onChange={handleChange}
                            required
                            className="mt-2 border-gray-300 focus:border-[#1B5E20] focus:ring-[#1B5E20]"
                            placeholder="e.g., Port of Felixstowe, UK"
                          />
                        </div>

                        <div>
                          <Label htmlFor="additionalInfo" className="text-[#333333] font-semibold">Additional Information</Label>
                          <Textarea
                            id="additionalInfo"
                            name="additionalInfo"
                            value={formData.additionalInfo}
                            onChange={handleChange}
                            rows={5}
                            className="mt-2 border-gray-300 focus:border-[#1B5E20] focus:ring-[#1B5E20]"
                            placeholder="Any specific requirements, delivery timelines, or questions..."
                          />
                        </div>
                      </div>

                      <Button
                        type="submit"
                        size="lg"
                        disabled={isSubmitting}
                        className="w-full bg-[#C8A951] hover:bg-[#B8994A] text-white font-semibold py-6 text-lg transition-all shadow-lg hover:shadow-xl"
                      >
                        {isSubmitting ? 'Submitting...' : (
                          <>
                            Submit Quote Request
                            <Send className="ml-2" size={20} />
                          </>
                        )}
                      </Button>

                      <p className="text-xs text-gray-500 text-center">
                        By submitting this form, you agree to be contacted by Folalabi Farms regarding your quote request.
                      </p>
                    </form>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Quote;
